import {
    FactureFormaliteApiObject,
    useFacturationService,
    useFormaliteService,
    useSociete,
} from '@europrocurement/l2d-domain';
import { useDownload, useLazyDataLoader } from '@europrocurement/l2d-modules/hooks';
import { enqueueSnackbar } from 'notistack';
import {
    collectionInvoiceApiToStateTransformer,
    CollectionInvoice,
    REMOVE_COLLECTION_INVOICE_CTX,
    UPDATE_COLLECTION_INVOICE_CTX,
    ADD_COLLECTION_INVOICE_CTX,
    useCollectionBillingCtxDispatch,
} from '../../../providers/CollectionBillingProvider';
import { useDocumentPreviewContext } from '../providers/DocumentPreviewContext';

const useBillingActions = () => {
    const { collectionApi } = useFormaliteService();
    const { factureApi, devisFormaliteApi } = useFacturationService();
    const xIdSociete = useSociete();
    const { download } = useDownload();
    const { setDocumentPreview } = useDocumentPreviewContext();
    const collectionInvoiceDispatch = useCollectionBillingCtxDispatch();
    const { refreshDossierPrefacturation } = useLazyDataLoader();

    const downloadInvoice = async (id: number): Promise<Blob> => {
        const requestParameters = { xIdSociete, id: id as unknown as string };

        const res = await factureApi.apiFacturesIdPdfGet(requestParameters, {
            responseType: 'blob',
        });

        return res.data as unknown as Blob;
    };

    const downloadEstimate = async (id: number): Promise<Blob> => {
        const requestParameters = { xIdSociete, id: id as unknown as string };

        const res = await devisFormaliteApi.apiDevisFormaliteIdPdfGet(requestParameters, {
            responseType: 'blob',
        });

        return res.data as unknown as Blob;
    };

    const handleDownloadInvoice = async (fileName: string, id: number): Promise<void> => {
        try {
            const fileContent = await downloadInvoice(id);

            download(fileName, fileContent);

            return Promise.resolve();
        } catch (e) {
            enqueueSnackbar('Une erreur est survenue lors du téléchargement de la facture', {
                variant: 'error',
            });
            console.error(e);

            return Promise.reject();
        }
    };

    const handleShowInvoice = async (fileName: string, id: number): Promise<void> => {
        try {
            const fileContent = await downloadInvoice(id);

            setDocumentPreview({
                fileName,
                fileType: 'pdf',
                content: fileContent,
            });

            return Promise.resolve();
        } catch (e) {
            enqueueSnackbar('Une erreur est survenue lors du téléchargement de la facture', {
                variant: 'error',
            });
            console.error(e);

            return Promise.reject();
        }
    };

    const handleDownloadEstimate = async (fileName: string, id: number): Promise<void> => {
        try {
            const fileContent = await downloadEstimate(id);

            download(fileName, fileContent);

            return Promise.resolve();
        } catch (e) {
            enqueueSnackbar('Une erreur est survenue lors du téléchargement du devis', {
                variant: 'error',
            });
            console.error(e);

            return Promise.reject();
        }
    };

    const handleShowEstimate = async (fileName: string, id: number): Promise<void> => {
        try {
            const fileContent = await downloadEstimate(id);

            setDocumentPreview({
                fileName,
                fileType: 'pdf',
                content: fileContent,
            });

            return Promise.resolve();
        } catch (e) {
            enqueueSnackbar('Une erreur est survenue lors du téléchargement du devis', {
                variant: 'error',
            });
            console.error(e);

            return Promise.reject();
        }
    };

    const handleDeleteInvoice = async (
        invoice: CollectionInvoice,
        dossierIds: number[],
    ): Promise<void> => {
        if (!invoice.id) {
            return Promise.reject(new Error('Invoice ID is required'));
        }

        if (!invoice.factureEnPreparation) {
            return Promise.reject(new Error('Invoice cannot be remove'));
        }

        try {
            await factureApi.apiFacturesIdDelete({
                id: invoice.id as unknown as string,
                xIdSociete,
            });
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Une erreur est survenue lors de la suppression de votre facture', {
                variant: 'error',
            });

            return Promise.reject();
        }

        enqueueSnackbar('Facture supprimée', {
            autoHideDuration: 2000,
        });

        if (collectionInvoiceDispatch) {
            collectionInvoiceDispatch({
                type: REMOVE_COLLECTION_INVOICE_CTX,
                payload: invoice.id,
            });
            refreshDossierPrefacturation(dossierIds);
        }

        return Promise.resolve();
    };

    const handleCreateCreditNote = async (
        invoice: CollectionInvoice,
        dossierIds: number[],
    ): Promise<void> => {
        if (!invoice.id) {
            return Promise.reject(new Error('Invoice ID is required'));
        }

        if (invoice.factureEnPreparation) {
            return Promise.reject(new Error('Cannot create credit note for this invoice'));
        }

        try {
            const responseCreditNote = await factureApi.apiFacturesIdAvoirPut({
                id: invoice.id as unknown as string,
                factureJsonld: {},
                xIdSociete,
            });

            if (collectionInvoiceDispatch) {
                collectionInvoiceDispatch({
                    type: ADD_COLLECTION_INVOICE_CTX,
                    payload: collectionInvoiceApiToStateTransformer(
                        responseCreditNote.data as FactureFormaliteApiObject,
                    ),
                });

                refreshDossierPrefacturation(dossierIds);
            }

            enqueueSnackbar("L'avoir a bien été créé", {
                autoHideDuration: 2000,
            });
        } catch (e) {
            console.error(e);
            enqueueSnackbar("Une erreur est survenue lors de la création de l'avoir", {
                variant: 'error',
            });

            return Promise.reject();
        }

        if (collectionInvoiceDispatch) {
            try {
                const updatedInvoice = await factureApi.apiFacturesIdGet({
                    id: invoice.id as unknown as string,
                    xIdSociete,
                });

                collectionInvoiceDispatch({
                    type: UPDATE_COLLECTION_INVOICE_CTX,
                    payload: collectionInvoiceApiToStateTransformer(
                        updatedInvoice.data as FactureFormaliteApiObject,
                    ),
                });
            } catch (e) {
                console.error(e);

                return Promise.reject();
            }
        }

        return Promise.resolve();
    };

    const handleValidateInvoice = async (invoice: CollectionInvoice): Promise<void> => {
        if (!invoice.id) {
            return Promise.reject(new Error('Invoice ID is required'));
        }

        if (!invoice.factureEnPreparation) {
            return Promise.reject(new Error('Cannot validate invoice'));
        }

        try {
            const response = await factureApi.apiFacturesIdValidatePut({
                id: invoice.id as unknown as string,
                factureJsonld: {},
                xIdSociete,
            });

            if (collectionInvoiceDispatch) {
                collectionInvoiceDispatch({
                    type: UPDATE_COLLECTION_INVOICE_CTX,
                    payload: collectionInvoiceApiToStateTransformer(
                        response.data as FactureFormaliteApiObject,
                    ),
                });
            }

            enqueueSnackbar('Facture validée', {
                autoHideDuration: 2000,
            });
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Une erreur est survenue lors de la validation de la facture', {
                variant: 'error',
            });

            return Promise.reject();
        }

        return Promise.resolve();
    };

    const handleRecipientChange = async (
        collectionId: number,
        recipientId: number,
    ): Promise<void> => {
        try {
            await collectionApi.apiCollectionsDossiersIdPatch({
                id: collectionId as unknown as string,
                collectionDeDossierCollectiondossierUpdate: {
                    client: recipientId as unknown as string,
                },
            });
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Une erreur est survenue lors de mise à jour', {
                variant: 'error',
            });

            return Promise.reject();
        }

        return Promise.resolve();
    };

    const handleBillingTypeChange = async (
        collectionId: number,
        billingType?: boolean,
    ): Promise<void> => {
        try {
            await collectionApi.apiCollectionsDossiersIdPatch({
                id: collectionId as unknown as string,
                collectionDeDossierCollectiondossierUpdate: {
                    venteIndirecte: billingType,
                },
            });
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Une erreur est survenue lors de mise à jour', {
                variant: 'error',
            });

            return Promise.reject();
        }

        return Promise.resolve();
    };

    return {
        downloadInvoice,
        handleDownloadInvoice,
        handleShowInvoice,
        handleDownloadEstimate,
        handleShowEstimate,
        handleDeleteInvoice,
        handleCreateCreditNote,
        handleValidateInvoice,
        handleRecipientChange,
        handleBillingTypeChange,
    };
};

export default useBillingActions;
